/*
  assets/littleg-family-data.js — FAMILY-DATA-POLICY-11
  Owner: Family-only privacy policy, private record adapter, Family Private social-graph adapter,
  temporary notice-draft adapter and parent-consent gate.
  Record persistence owner: assets/littleg-city-result-storage.js.
  Notice draft + Family social-graph facade/cache owner: this file; consumers must use
  LittleGFamilyData and must not access those keys directly.
  F02 rule: Route Readiness is stored only as a sanitized child of an existing private Notice record.
  Boundary: no enterprise handoff, GFun, public publication, precise-home-location storage,
  authentication authority or professional report ownership. Customer Memory writes use LittleGWebSession only.
  Kid Map location contract: station_anchor is canonical for new rows; legacy_approximate remains readable.
*/
(function (window, document) {
  'use strict';

  var VERSION = '2026-09-23-family-data-policy-11';
  if (window.__LITTLEG_FAMILY_DATA_POLICY__ === VERSION) return;
  window.__LITTLEG_FAMILY_DATA_POLICY__ = VERSION;

  var GATE_KEY = 'littleg:family:parent-gate:v1';
  var DRAFT_KEY = 'littleg:family:notice-draft:v1';
  var SOCIAL_KEY = 'littleg_kid_map_social_places_v1';
  var CLOUD_SNAPSHOT_PATH = '/v1/family/customer-memory/snapshot';
  var CLOUD_IMPORT_PATH = '/v1/family/customer-memory/import';
  var CLOUD_ENTITIES_PATH = '/v1/family/customer-memory/entities';
  var CLOUD_SOURCE_TYPE = 'kid_map';
  var CLOUD_CONSENT_VERSION = 'kid-map-cloud-sync-v1';
  var cloudHydratePromise = null;
  var cloudEntityIds = Object.create(null);
  var cloudSessionGeneration = 0;
  var cvCloudSubject = '';
  var cvCloudState = 'idle';
  var stationIndexPromise = null;
  var trustedStationIndex = null;
  var suppressCloudMutation = false;
  var GATE_TTL_MS = 10 * 60 * 1000;
  var DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  var SOCIAL_MAX_ROWS = 60;
  var SOCIAL_LOCATION_TYPES = Object.freeze(['station_anchor', 'legacy_approximate']);
  var SOCIAL_MEMORY_VIEWS = Object.freeze(['friends', 'alumni', 'class', 'family', 'travel']);
  var SOCIAL_MARKER_EMOJIS = Object.freeze(['⭐', '🐶', '🐱', '⚽', '🎮', '🌈', '🚀', '❤️']);
  var ALLOWED_RECORD_TYPES = Object.freeze(['mission', 'notice']);
  var ROUTE_CONFIDENCE = Object.freeze(['unknown', 'estimated', 'verified']);
  var ROUTE_READINESS_STATES = Object.freeze([
    'context_only',
    'arrival_deadline_set',
    'route_window_estimated',
    'ready',
  ]);

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }
  function safeSession() {
    try {
      return window.sessionStorage || null;
    } catch (_) {
      return null;
    }
  }
  function safeLocal() {
    try {
      return window.localStorage || null;
    } catch (_) {
      return null;
    }
  }
  function cloneJson(value) {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_) {
      return null;
    }
  }
  function finiteCoordinate(value, min, max) {
    var number = Number(value);
    return Number.isFinite(number) && number >= min && number <= max ? number : null;
  }

  function cleanStringList(value, maxItems, maxLength) {
    if (!Array.isArray(value)) return [];
    return value
      .slice(0, maxItems || 8)
      .map(function (item) {
        return clean(item).slice(0, maxLength || 80);
      })
      .filter(Boolean);
  }
  function boundedInteger(value, fallback, maximum) {
    var parsed = Number.parseInt(String(value == null ? '' : value), 10);
    if (!Number.isFinite(parsed) || parsed < 0) return fallback;
    return Math.min(parsed, maximum);
  }
  function allowed(value, choices, fallback) {
    var key = clean(value);
    return choices.indexOf(key) !== -1 ? key : fallback;
  }
  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
    } catch (_) {}
  }
  function routeParty(value) {
    var source = value && typeof value === 'object' ? value : {};
    return {
      guardian: clean(source.guardian).slice(0, 80) || null,
      note: clean(source.note).slice(0, 240) || null,
    };
  }
  function normalizeRouteReadiness(input, noticeRecordId) {
    if (!input || typeof input !== 'object') return null;
    var source = cloneJson(input) || {};
    var routeFact =
      source.route_fact && typeof source.route_fact === 'object' ? source.route_fact : {};
    var routeSource =
      clean(routeFact.source) === 'trusted_route_provider' ? 'trusted_route_provider' : 'none';
    var trustedRoute = routeSource === 'trusted_route_provider';
    var duration = trustedRoute ? boundedInteger(routeFact.duration_minutes, null, 600) : null;
    var packing =
      source.packing_cross_check && typeof source.packing_cross_check === 'object'
        ? source.packing_cross_check
        : {};
    var total = boundedInteger(packing.total, 0, 200);
    var incomplete = Math.min(total, boundedInteger(packing.incomplete, total, 200));
    var destination = clean(source.destination_text).slice(0, 240);
    var arrival = clean(source.arrival_target).slice(0, 64) || null;
    var requestedState = allowed(
      source.readiness_state,
      ROUTE_READINESS_STATES,
      arrival ? 'arrival_deadline_set' : 'context_only'
    );
    var readinessState = requestedState;
    if (
      !trustedRoute &&
      (requestedState === 'route_window_estimated' || requestedState === 'ready')
    )
      readinessState = arrival ? 'arrival_deadline_set' : 'context_only';
    var departure =
      source.departure_window && typeof source.departure_window === 'object'
        ? source.departure_window
        : {};
    var entrance = source.entrance && typeof source.entrance === 'object' ? source.entrance : {};
    var last500 = source.last_500m && typeof source.last_500m === 'object' ? source.last_500m : {};
    var publicContext =
      source.public_context && typeof source.public_context === 'object'
        ? source.public_context
        : {};
    return {
      version: 1,
      notice_record_id: clean(source.notice_record_id || noticeRecordId).slice(0, 120),
      destination_text: destination,
      arrival_target: arrival,
      departure_window: {
        recommended_at: trustedRoute ? clean(departure.recommended_at).slice(0, 64) || null : null,
        leave_by: trustedRoute ? clean(departure.leave_by).slice(0, 64) || null : null,
      },
      route_fact: {
        duration_minutes: duration,
        source: routeSource,
        confidence: trustedRoute
          ? allowed(routeFact.confidence, ROUTE_CONFIDENCE, 'estimated')
          : 'unknown',
      },
      entrance: {
        status: allowed(entrance.status, ['unknown', 'unverified', 'verified'], 'unknown'),
        label: clean(entrance.label).slice(0, 160) || null,
        confidence: allowed(entrance.confidence, ROUTE_CONFIDENCE, 'unknown'),
      },
      last_500m: {
        status: allowed(last500.status, ['unknown', 'unverified', 'verified'], 'unknown'),
        instruction: clean(last500.instruction).slice(0, 320) || null,
        confidence: allowed(last500.confidence, ROUTE_CONFIDENCE, 'unknown'),
      },
      dropoff: routeParty(source.dropoff),
      pickup: routeParty(source.pickup),
      guardian_responsibility: clean(source.guardian_responsibility).slice(0, 160) || null,
      packing_cross_check: {
        total: total,
        incomplete: incomplete,
        ready: total > 0 && incomplete === 0,
      },
      public_context: {
        data_state: clean(publicContext.data_state).slice(0, 64) || 'unknown',
        traffic_state: clean(publicContext.traffic_state).slice(0, 64) || 'unknown',
        weather_state: clean(publicContext.weather_state).slice(0, 64) || 'unknown',
        holiday_state: clean(publicContext.holiday_state).slice(0, 64) || 'unknown',
      },
      readiness_state: readinessState,
      precise_location_saved: false,
      updated_at: clean(source.updated_at).slice(0, 64) || new Date().toISOString(),
    };
  }

  function readGate() {
    var store = safeSession();
    if (!store) return null;
    try {
      var value = JSON.parse(store.getItem(GATE_KEY) || 'null');
      if (!value || typeof value !== 'object') return null;
      if (!Number.isFinite(Number(value.expires_at)) || Number(value.expires_at) <= Date.now()) {
        store.removeItem(GATE_KEY);
        return null;
      }
      return value;
    } catch (_) {
      return null;
    }
  }
  function writeGate(value) {
    var store = safeSession();
    if (!store) return false;
    try {
      store.setItem(GATE_KEY, JSON.stringify(value));
      return true;
    } catch (_) {
      return false;
    }
  }
  function normalizeDraft(input) {
    if (!input || typeof input !== 'object') return null;
    var source = cloneJson(input);
    if (!source || typeof source !== 'object') return null;
    delete source.file;
    delete source.file_blob;
    delete source.blob;
    delete source.token;
    delete source.access_token;
    delete source.precise_location;
    source.version = 1;
    source.storage_mode = 'local_private';
    source.updated_at = new Date().toISOString();
    source.expires_at = Date.now() + DRAFT_TTL_MS;
    return source;
  }
  function readDraft() {
    var store = safeLocal();
    if (!store) return null;
    try {
      var value = JSON.parse(store.getItem(DRAFT_KEY) || 'null');
      if (!value || typeof value !== 'object') return null;
      if (!Number.isFinite(Number(value.expires_at)) || Number(value.expires_at) <= Date.now()) {
        store.removeItem(DRAFT_KEY);
        emit('littleg:notice-draft-change', { available: false, reason: 'expired' });
        return null;
      }
      return cloneJson(value);
    } catch (_) {
      return null;
    }
  }
  function writeDraft(input) {
    var store = safeLocal();
    var draft = normalizeDraft(input);
    if (!store || !draft) return null;
    try {
      store.setItem(DRAFT_KEY, JSON.stringify(draft));
      emit('littleg:notice-draft-change', { available: true, reason: 'saved' });
      return cloneJson(draft);
    } catch (_) {
      return null;
    }
  }
  function clearDraft(reason) {
    var store = safeLocal();
    try {
      if (store) store.removeItem(DRAFT_KEY);
    } catch (_) {}
    emit('littleg:notice-draft-change', { available: false, reason: clean(reason || 'cleared') });
    return true;
  }
  function cloudSession() {
    var session = window.LittleGWebSession;
    return session &&
      typeof session.authenticatedFetch === 'function' &&
      typeof session.getState === 'function'
      ? session
      : null;
  }

  function cloudUrl(path) {
    var session = cloudSession();
    var base = clean((session && session.apiBase) || 'https://api.glhksolution.com').replace(
      /\/+$/,
      ''
    );
    return base + path;
  }

  async function cloudJson(path, options) {
    var session = cloudSession();
    if (!session) return null;

    // 捕获发起请求时的会话代数 (Generation)，防止并发登出导致的旧请求继续执行
    var startGeneration = cloudSessionGeneration;

    if (typeof session.ready === 'function') await session.ready();

    // 会话状态改变，或已不再是 authenticated 状态
    if (
      startGeneration !== cloudSessionGeneration ||
      session.getState().status !== 'authenticated'
    ) {
      return null;
    }

    var response = await session.authenticatedFetch(cloudUrl(path), options || { method: 'GET' });

    // 网络请求完成后再次检查会话代数
    if (
      startGeneration !== cloudSessionGeneration ||
      session.getState().status !== 'authenticated'
    ) {
      return null;
    }

    var payload = await response.json().catch(function () {
      return null;
    });
    if (
      startGeneration !== cloudSessionGeneration ||
      session.getState().status !== 'authenticated'
    ) {
      return null;
    }
    if (!response.ok || !payload || payload.success !== true) {
      var error = new Error(
        (payload && payload.error && payload.error.message) || 'Family cloud request failed.'
      );
      error.code =
        (payload && payload.error && payload.error.code) || 'family_cloud_request_failed';
      error.status = response.status;
      throw error;
    }
    return payload.data && typeof payload.data === 'object' ? payload.data : {};
  }

  function stationDatasetUrl() {
    var config =
      document.querySelector('[data-littleg-kid-map-config]') ||
      document.querySelector('[data-littleg-family-data-config]');
    return clean(config && config.getAttribute('data-station-dataset-url'));
  }

  function loadStationIndex() {
    if (stationIndexPromise) return stationIndexPromise;
    stationIndexPromise = Promise.resolve()
      .then(async function () {
        var url = stationDatasetUrl();
        if (!url) return Object.create(null);
        var response = await window.fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Station dataset is unavailable.');
        var geojson = await response.json();
        var index = Object.create(null);
        (Array.isArray(geojson && geojson.features) ? geojson.features : []).forEach(
          function (feature) {
            var props = (feature && feature.properties) || {};
            var coordinates = feature && feature.geometry && feature.geometry.coordinates;
            var stationId = clean(props.station_id).slice(0, 120);
            if (!stationId || !Array.isArray(coordinates) || coordinates.length < 2) return;
            var lng = finiteCoordinate(coordinates[0], -180, 180);
            var lat = finiteCoordinate(coordinates[1], -90, 90);
            if (lat === null || lng === null) return;
            index[stationId] = {
              stationId: stationId,
              stationName: clean(props.name_en).slice(0, 120),
              stationNameZhHk: clean(props.name_zh_hk).slice(0, 120),
              stationLineIds: cleanStringList(props.line_ids, 8, 80),
              lat: lat,
              lng: lng,
            };
          }
        );
        if (Object.keys(index).length) trustedStationIndex = index;
        return index;
      })
      .catch(function (error) {
        stationIndexPromise = null;
        throw error;
      });
    return stationIndexPromise;
  }

  function cloudEligible(row) {
    return (
      !!row &&
      clean(row.id) &&
      clean(row.locationType || row.location_type) === 'station_anchor' &&
      clean(row.stationId || row.station_id) &&
      row.privacyConfirmed === true &&
      row.parentConfirmed === true &&
      clean(row.approvalStatus) === 'approved'
    );
  }

  function cloudEntityPayload(row) {
    if (!cloudEligible(row)) return null;
    var friend = clean(row.type || row.kind) !== 'mine';
    var marker = friend
      ? allowed(row.markerEmoji || row.marker_emoji, SOCIAL_MARKER_EMOJIS, '⭐')
      : '🏠';
    return {
      entity_type: friend ? 'person' : 'place',
      relationship_type: friend
        ? allowed(row.relationship, ['friend', 'classmate', 'family'], 'friend')
        : null,
      display_label: marker,
      anchor_type: 'station_anchor',
      anchor_ref: clean(row.stationId || row.station_id).slice(0, 120),
      source_type: CLOUD_SOURCE_TYPE,
      source_ref: clean(row.id).slice(0, 120),
      memory_views: cleanStringList(row.memoryViews || row.memory_views, 5, 24).filter(
        function (view) {
          return SOCIAL_MEMORY_VIEWS.indexOf(view) >= 0;
        }
      ),
    };
  }

  async function cloudRowFromEntity(entity, currentById, stationIndex) {
    if (
      !entity ||
      entity.source_type !== CLOUD_SOURCE_TYPE ||
      entity.anchor_type !== 'station_anchor'
    )
      return null;
    var localId = clean(entity.source_ref).slice(0, 120);
    var stationId = clean(entity.anchor_ref).slice(0, 120);
    if (!localId || !stationId) return null;
    var station = stationIndex[stationId];
    var current = currentById[localId];
    if (!station && current && clean(current.stationId || current.station_id) === stationId) {
      station = {
        stationId: stationId,
        stationName: current.stationName || '',
        stationNameZhHk: current.stationNameZhHk || '',
        stationLineIds: current.stationLineIds || [],
        lat: current.lat,
        lng: current.lng,
      };
    }
    if (!station) return null;

    var friend = entity.entity_type !== 'place';
    return normalizeSocialPlace({
      id: localId,
      type: friend ? 'friend' : 'mine',
      kind: friend ? 'friend' : 'mine',
      relationship: friend ? entity.relationship_type : '',
      markerEmoji: friend ? allowed(entity.display_label, SOCIAL_MARKER_EMOJIS, '⭐') : '',
      marker_emoji: friend ? allowed(entity.display_label, SOCIAL_MARKER_EMOJIS, '⭐') : '',
      memoryViews:
        Array.isArray(entity.memory_views) && entity.memory_views.length
          ? entity.memory_views
          : ['friends'],
      memory_views:
        Array.isArray(entity.memory_views) && entity.memory_views.length
          ? entity.memory_views
          : ['friends'],
      locationType: 'station_anchor',
      stationId: station.stationId,
      stationName: station.stationName,
      stationNameZhHk: station.stationNameZhHk,
      stationLineIds: station.stationLineIds,
      lat: station.lat,
      lng: station.lng,
      visibility: 'family_private',
      privacyConfirmed: true,
      parentConfirmed: true,
      approvalStatus: 'approved',
      cloudEntityId: clean(entity.entity_id).slice(0, 120),
      cloudSyncState: 'synced',
      createdAt: clean(entity.created_at),
      updatedAt: clean(entity.updated_at),
    });
  }

  async function importCloudRows(rows) {
    var entities = (Array.isArray(rows) ? rows : []).map(cloudEntityPayload).filter(Boolean);
    if (!entities.length) return null;
    return cloudJson(CLOUD_IMPORT_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ consent_version: CLOUD_CONSENT_VERSION, entities: entities }),
    });
  }

  async function readCloudSnapshot() {
    return cloudJson(CLOUD_SNAPSHOT_PATH, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
  }

  function applyCloudRows(rows) {
    suppressCloudMutation = true;
    try {
      return writeSocialPlaces(rows, 'cloud-hydrate');
    } finally {
      suppressCloudMutation = false;
    }
  }

  function transitionSocialGraphToGuest() {
    cloudSessionGeneration += 1;
    cloudHydratePromise = null;
    cloudEntityIds = Object.create(null);

    var rows = readSocialPlaces();
    var localRows = rows.filter(function (row) {
      return !clean(row && row.cloudEntityId) && clean(row && row.cloudSyncState) !== 'synced';
    });

    if (localRows.length !== rows.length) {
      suppressCloudMutation = true;
      try {
        writeSocialPlaces(localRows, 'session-guest');
      } finally {
        suppressCloudMutation = false;
      }
    }

    emit('littleg:family-cloud-sync', {
      state: 'guest',
      cloud_count: 0,
      local_count: localRows.length,
    });

    return localRows;
  }

  async function hydrateSocialGraphFromCloud(force) {
    if (cloudHydratePromise && !force) return cloudHydratePromise;
    var hydrateGeneration = cloudSessionGeneration;
    cvCloudState = 'loading';
    cloudHydratePromise = Promise.resolve()
      .then(async function () {
        var session = cloudSession();
        if (!session) return readSocialPlaces();
        if (typeof session.ready === 'function') await session.ready();
        if (
          hydrateGeneration !== cloudSessionGeneration ||
          session.getState().status !== 'authenticated'
        )
          return readSocialPlaces();

        var localRows = readSocialPlaces();
        var currentById = Object.create(null);
        localRows.forEach(function (row) {
          currentById[row.id] = row;
        });

        var snapshot = await readCloudSnapshot();
        if (
          hydrateGeneration !== cloudSessionGeneration ||
          session.getState().status !== 'authenticated'
        )
          return readSocialPlaces();

        var cloudEntities = Array.isArray(snapshot && snapshot.entities)
          ? snapshot.entities.filter(function (entity) {
              return entity && entity.source_type === CLOUD_SOURCE_TYPE;
            })
          : [];
        var cloudSourceRefs = new Set(
          cloudEntities.map(function (entity) {
            return clean(entity.source_ref);
          })
        );
        var pending = localRows.filter(function (row) {
          return (
            cloudEligible(row) &&
            (!cloudSourceRefs.has(clean(row.id)) || row.cloudSyncState === 'pending')
          );
        });

        if (pending.length) {
          await importCloudRows(pending);
          snapshot = await readCloudSnapshot();
          if (
            hydrateGeneration !== cloudSessionGeneration ||
            session.getState().status !== 'authenticated'
          )
            return readSocialPlaces();

          cloudEntities = Array.isArray(snapshot && snapshot.entities)
            ? snapshot.entities.filter(function (entity) {
                return entity && entity.source_type === CLOUD_SOURCE_TYPE;
              })
            : [];
        }

        var stations = await loadStationIndex();
        if (
          hydrateGeneration !== cloudSessionGeneration ||
          session.getState().status !== 'authenticated'
        )
          return readSocialPlaces();

        var hydrated = [];
        for (var i = 0; i < cloudEntities.length; i += 1) {
          var row = await cloudRowFromEntity(cloudEntities[i], currentById, stations);
          if (
            hydrateGeneration !== cloudSessionGeneration ||
            session.getState().status !== 'authenticated'
          )
            return readSocialPlaces();

          if (row) {
            cloudEntityIds[row.id] = row.cloudEntityId;
            hydrated.push(row);
          }
        }

        var legacy = localRows.filter(function (row) {
          return clean(row.locationType || row.location_type) === 'legacy_approximate';
        });

        if (
          hydrateGeneration !== cloudSessionGeneration ||
          session.getState().status !== 'authenticated'
        )
          return readSocialPlaces();

        cvCloudState = 'ready';
        cvCloudSubject = clean(session.getState().profile && session.getState().profile.id);
        applyCloudRows(hydrated.concat(legacy).slice(0, SOCIAL_MAX_ROWS));
        emit('littleg:family-cloud-sync', {
          state: 'ready',
          cloud_count: hydrated.length,
          legacy_local_count: legacy.length,
        });
        return readSocialPlaces();
      })
      .catch(function (error) {
        if (hydrateGeneration !== cloudSessionGeneration) return readSocialPlaces();
        cvCloudState = 'unavailable';
        emit('littleg:family-cloud-sync', {
          state: 'error',
          code: clean((error && error.code) || (error && error.message) || 'cloud_sync_failed'),
        });
        return readSocialPlaces();
      });
    return cloudHydratePromise;
  }

  async function syncCloudRow(row, expectedGeneration) {
    if (!cloudEligible(row)) return null;
    if (expectedGeneration !== cloudSessionGeneration) return null;
    var result = await importCloudRows([row]);
    if (expectedGeneration !== cloudSessionGeneration) return null;
    var imported = result && Array.isArray(result.imported) ? result.imported[0] : null;
    var entityId = clean(imported && imported.entity_id);
    if (!entityId) return null;
    cloudEntityIds[row.id] = entityId;
    var current = socialGetPlace(row.id);
    if (current) {
      current.cloudEntityId = entityId;
      current.cloudSyncState = 'synced';
      suppressCloudMutation = true;
      try {
        socialUpsertPlace(current);
      } finally {
        suppressCloudMutation = false;
      }
    }
    emit('littleg:family-cloud-sync', { state: 'saved', changed_id: row.id });
    return entityId;
  }

  async function deleteCloudRow(localId, entityId, expectedGeneration) {
    if (expectedGeneration !== cloudSessionGeneration) return false;
    var id = clean(entityId || cloudEntityIds[localId]);
    if (!id) {
      var snapshot = await readCloudSnapshot();
      if (expectedGeneration !== cloudSessionGeneration) return false;
      var match = Array.isArray(snapshot && snapshot.entities)
        ? snapshot.entities.find(function (entity) {
            return (
              entity &&
              entity.source_type === CLOUD_SOURCE_TYPE &&
              clean(entity.source_ref) === clean(localId)
            );
          })
        : null;
      id = clean(match && match.entity_id);
    }
    if (!id) return false;

    var deleted = await cloudJson(CLOUD_ENTITIES_PATH + '/' + encodeURIComponent(id), {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
    });

    // A stale session or an unconfirmed delete must never clear the local cloud binding.
    if (expectedGeneration !== cloudSessionGeneration || !deleted || deleted.deleted !== true)
      return false;

    delete cloudEntityIds[localId];
    emit('littleg:family-cloud-sync', { state: 'deleted', changed_id: localId });
    return true;
  }

  function scheduleCloudMutation(reason, changedId, priorRow) {
    if (suppressCloudMutation) return;
    var session = cloudSession();
    if (!session) return;
    var mutationGeneration = cloudSessionGeneration;
    Promise.resolve()
      .then(async function () {
        if (typeof session.ready === 'function') await session.ready();
        if (
          mutationGeneration !== cloudSessionGeneration ||
          session.getState().status !== 'authenticated'
        )
          return;
        if (reason === 'remove') {
          await deleteCloudRow(changedId, priorRow && priorRow.cloudEntityId, mutationGeneration);
          return;
        }
        if (reason === 'clear' || reason === 'replace') {
          if (mutationGeneration !== cloudSessionGeneration) return;
          await hydrateSocialGraphFromCloud(true);
          return;
        }
        var row = socialGetPlace(changedId);
        if (row && cloudEligible(row)) {
          await syncCloudRow(row, mutationGeneration);
          return;
        }
        if (priorRow && cloudEligible(priorRow)) {
          await deleteCloudRow(changedId, priorRow.cloudEntityId, mutationGeneration);
        }
      })
      .catch(function (error) {
        if (mutationGeneration !== cloudSessionGeneration) return;
        emit('littleg:family-cloud-sync', {
          state: 'error',
          changed_id: clean(changedId),
          code: clean((error && error.code) || (error && error.message) || 'cloud_sync_failed'),
        });
      });
  }

  function normalizeSocialPlace(input) {
    if (!input || typeof input !== 'object') return null;
    var row = cloneJson(input);
    if (!row || typeof row !== 'object') return null;
    var type = clean(row.type || row.kind) === 'mine' ? 'mine' : 'friend';
    var id = clean(row.id).slice(0, 120);
    if (!id) return null;
    var requestedLocationType = clean(row.locationType || row.location_type);
    var locationType =
      requestedLocationType === 'legacy_approximate' ? 'legacy_approximate' : 'station_anchor';

    row.id = id;
    row.type = type;
    row.kind = type;
    row.locationType = locationType;
    row.location_type = locationType;
    row.visibility = 'family_private';

    var requestedMemoryViews = cleanStringList(row.memoryViews || row.memory_views, 5, 24).filter(
      function (view, index, list) {
        return SOCIAL_MEMORY_VIEWS.indexOf(view) >= 0 && list.indexOf(view) === index;
      }
    );

    row.memoryViews = requestedMemoryViews.length ? requestedMemoryViews : ['friends'];
    row.memory_views = row.memoryViews.slice();

    row.relationship =
      type === 'friend'
        ? allowed(row.relationship, ['friend', 'classmate', 'family'], 'friend')
        : '';

    var requestedMarkerEmoji = clean(row.markerEmoji || row.marker_emoji);
    row.markerEmoji =
      type === 'friend' ? allowed(requestedMarkerEmoji, SOCIAL_MARKER_EMOJIS, '⭐') : '';
    row.marker_emoji = row.markerEmoji;

    row.label =
      type === 'mine'
        ? '🏠 我的地方'
        : row.relationship === 'classmate'
          ? '🏫 同學'
          : row.relationship === 'family'
            ? '🏡 家人'
            : '⭐ 好朋友';

    delete row.name;
    delete row.friendName;
    delete row.friend_name;
    delete row.displayName;
    delete row.display_name;
    delete row.fullName;
    delete row.full_name;

    row.privacyConfirmed = row.privacyConfirmed === true;
    row.parentConfirmed = row.parentConfirmed === true;

    var requestedApprovalStatus = allowed(row.approvalStatus, ['pending', 'approved'], 'pending');
    row.approvalStatus =
      row.parentConfirmed && requestedApprovalStatus === 'approved' ? 'approved' : 'pending';

    delete row.exact_address;
    delete row.exactAddress;
    delete row.home_lat;
    delete row.home_lng;
    delete row.homeLat;
    delete row.homeLng;
    delete row.precise_location;
    delete row.preciseLocation;
    delete row.address;
    delete row.homeAddress;
    delete row.home_address;
    delete row.school;
    delete row.schoolName;
    delete row.school_name;
    delete row.className;
    delete row.class_name;
    delete row.phone;
    delete row.mobile;
    delete row.email;
    delete row.birthday;
    delete row.birthDate;
    delete row.birth_date;
    delete row.avatar;
    delete row.avatarUrl;
    delete row.avatar_url;
    delete row.photo;
    delete row.photoUrl;
    delete row.photo_url;
    delete row.note;
    delete row.notes;
    delete row.guardian;
    delete row.guardianName;
    delete row.guardian_name;

    if (locationType === 'station_anchor') {
      var stationId = clean(row.stationId || row.station_id).slice(0, 120);
      var lat = finiteCoordinate(row.lat, -90, 90);
      var lng = finiteCoordinate(row.lng, -180, 180);
      if (!stationId || lat === null || lng === null) return null;
      row.stationId = stationId;
      row.station_id = stationId;
      row.stationName = clean(row.stationName || row.station_name).slice(0, 120);
      row.stationNameZhHk = clean(row.stationNameZhHk || row.station_name_zh_hk).slice(0, 120);
      row.stationLineIds = cleanStringList(row.stationLineIds || row.station_line_ids, 8, 80);
      row.lat = lat;
      row.lng = lng;
      row.precision = 'station';
      row.approximationMode = 'station_anchor';
      row.stationAnchor = true;
    } else {
      row.stationAnchor = false;
      row.stationId = '';
      row.station_id = '';
      row.stationName = '';
      row.stationNameZhHk = '';
      row.stationLineIds = [];
    }

    row.publicSearchable = false;
    row.childPublicProfile = false;
    row.realtimeChild = false;
    row.preciseLocationSaved = false;
    return row;
  }

  function canonicalStationRow(input) {
    var row = normalizeSocialPlace(input);
    if (!row || row.locationType !== 'station_anchor') return row;
    var station = trustedStationIndex && trustedStationIndex[row.stationId];
    if (!station) return null;
    row.stationId = station.stationId;
    row.station_id = station.stationId;
    row.stationName = station.stationName;
    row.stationNameZhHk = station.stationNameZhHk;
    row.stationLineIds = station.stationLineIds.slice();
    row.lat = station.lat;
    row.lng = station.lng;
    return row;
  }

  function readSocialPlaces(strict) {
    var store = safeLocal();
    if (!store) {
      if (strict) throw new Error('family_records_unavailable');
      return [];
    }
    try {
      var rows = JSON.parse(store.getItem(SOCIAL_KEY) || '[]');
      if (!Array.isArray(rows)) {
        if (strict) throw new Error('family_records_invalid');
        return [];
      }
      return rows
        .slice(0, SOCIAL_MAX_ROWS)
        .map(function (input) {
          var row = normalizeSocialPlace(input);
          if (!row || row.locationType !== 'station_anchor') return row;
          return trustedStationIndex && trustedStationIndex[row.stationId]
            ? canonicalStationRow(row)
            : row;
        })
        .filter(Boolean);
    } catch (_) {
      if (strict) throw new Error('family_records_unavailable');
      return [];
    }
  }

  function writeSocialPlaces(rows, reason, changedId, priorRow) {
    var store = safeLocal();
    if (!store || !Array.isArray(rows)) return false;
    var existingById = Object.create(null);
    try {
      var stored = JSON.parse(store.getItem(SOCIAL_KEY) || '[]');
      if (Array.isArray(stored))
        stored.forEach(function (entry) {
          if (entry && typeof entry === 'object') existingById[clean(entry.id)] = entry;
        });
    } catch (_) {}
    var safeRows = rows.slice(0, SOCIAL_MAX_ROWS).map(function (input) {
      var row = normalizeSocialPlace(input);
      if (!row || row.locationType !== 'station_anchor') return row;
      if (trustedStationIndex) return canonicalStationRow(row);
      var existing = normalizeSocialPlace(existingById[row.id]);
      return existing &&
        clean(existing.stationId || existing.station_id) === row.stationId &&
        Number(existing.lat) === row.lat &&
        Number(existing.lng) === row.lng &&
        existing.privacyConfirmed === row.privacyConfirmed &&
        existing.parentConfirmed === row.parentConfirmed &&
        existing.approvalStatus === row.approvalStatus
        ? row
        : null;
    });
    if (
      safeRows.some(function (row) {
        return !row;
      })
    )
      return false;
    try {
      store.setItem(SOCIAL_KEY, JSON.stringify(safeRows));
      emit('littleg:family-social-graph-change', {
        reason: clean(reason || 'replace'),
        count: safeRows.length,
      });
      scheduleCloudMutation(clean(reason || 'replace'), changedId, priorRow);
      return true;
    } catch (_) {
      return false;
    }
  }

  function socialGetPlace(id) {
    var key = clean(id);
    if (!key) return null;
    return (
      readSocialPlaces().find(function (row) {
        return clean(row.id) === key;
      }) || null
    );
  }

  function socialUpsertPlace(input) {
    var row = canonicalStationRow(input);
    if (!row) return null;
    if (cloudEligible(row) && !suppressCloudMutation) row.cloudSyncState = 'pending';
    var rows = readSocialPlaces();
    var index = rows.findIndex(function (entry) {
      return entry.id === row.id;
    });
    var priorRow = index >= 0 ? cloneJson(rows[index]) : null;

    if (index >= 0) {
      rows.splice(index, 1, row);
    } else {
      rows.unshift(row);
    }
    return writeSocialPlaces(rows, index >= 0 ? 'update' : 'create', row.id, priorRow)
      ? cloneJson(row)
      : null;
  }

  function socialRemovePlace(id) {
    var key = clean(id);
    if (!key) return false;
    var rows = readSocialPlaces();
    var priorRow =
      rows.find(function (row) {
        return row.id === key;
      }) || null;
    var next = rows.filter(function (row) {
      return row.id !== key;
    });
    if (next.length === rows.length) return false;
    return writeSocialPlaces(next, 'remove', key, priorRow);
  }

  function landmarkIdOf(row) {
    if (!row || typeof row !== 'object') return '';
    return clean(row.landmarkId || row.publicPlaceId || row.public_place_id || row.estate_id);
  }

  function socialFindByLandmarkId(placeId) {
    var key = clean(placeId);
    if (!key) return null;
    return (
      readSocialPlaces().find(function (row) {
        return landmarkIdOf(row) === key;
      }) || null
    );
  }

  function socialShareSummary() {
    var rows = readSocialPlaces();
    var friendCount = rows.filter(function (row) {
      return (
        row.type === 'friend' &&
        row.visibility === 'family_private' &&
        row.privacyConfirmed === true &&
        row.parentConfirmed === true &&
        row.approvalStatus === 'approved'
      );
    }).length;
    return {
      version: 1,
      friend_count: friendCount,
      share_mode: 'anonymous_constellation',
      contains_names: false,
      contains_station_ids: false,
      contains_station_names: false,
      contains_coordinates: false,
      contains_addresses: false,
    };
  }

  function socialMarkMissionComplete(placeId, data) {
    var key = clean(placeId);
    if (!key) return null;
    var rows = readSocialPlaces();
    var index = rows.findIndex(function (row) {
      return landmarkIdOf(row) === key;
    });
    if (index < 0) return null;

    var source = data && typeof data === 'object' ? data : {};
    var completedAt = clean(source.completed_at || source.completedAt) || new Date().toISOString();

    var row = Object.assign({}, rows[index], {
      missionCompleted: true,
      mission_completed: true,
      missionCompletedAt: completedAt,
      completed_at: completedAt,
      missionRoundKey: clean(source.round_key || source.roundKey).slice(0, 160),
      missionHandle: clean(source.mission_handle || source.missionHandle).slice(0, 160),
      updatedAt: new Date().toISOString(),
    });

    rows.splice(index, 1, row);
    return writeSocialPlaces(rows, 'mission-complete', row.id) ? cloneJson(row) : null;
  }

  function passportYearOf(value) {
    var parsed = Date.parse(clean(value));
    if (!Number.isFinite(parsed)) return '';
    return String(new Date(parsed).getUTCFullYear());
  }

  function passportMemoryViews(row) {
    return cleanStringList(row && (row.memoryViews || row.memory_views), 5, 24).filter(
      function (view, index, list) {
        return SOCIAL_MEMORY_VIEWS.indexOf(view) >= 0 && list.indexOf(view) === index;
      }
    );
  }

  function emptyPassportYearRecord() {
    return {
      summary: { friends: 0, alumni: 0, classes: 0, travel: 0, family: 0, growth: 0 },
      events: [],
    };
  }

  function socialPassportSnapshot() {
    var rows = readSocialPlaces();
    var records = Object.create(null);

    function recordFor(year) {
      if (!records[year]) records[year] = emptyPassportYearRecord();
      return records[year];
    }

    rows.forEach(function (row) {
      if (!row || row.visibility !== 'family_private') return;
      if (row.privacyConfirmed !== true || row.parentConfirmed !== true) return;
      if (row.approvalStatus !== 'approved') return;

      var occurredAt = clean(row.createdAt || row.created_at);
      var year = passportYearOf(occurredAt);
      if (!year) return;

      var record = recordFor(year);
      var views = passportMemoryViews(row);
      var isFriend = clean(row.type || row.kind) === 'friend';
      var relationship = clean(row.relationship);

      if (isFriend) record.summary.friends += 1;
      if (views.indexOf('alumni') >= 0 || relationship === 'alumni') record.summary.alumni += 1;
      if (views.indexOf('class') >= 0 || relationship === 'classmate') record.summary.classes += 1;
      if (views.indexOf('travel') >= 0) record.summary.travel += 1;
      if (views.indexOf('family') >= 0 || relationship === 'family') record.summary.family += 1;

      var eventView =
        views.find(function (view) {
          return view !== 'friends';
        }) || (isFriend ? 'friends' : '');
      if (eventView) {
        record.events.push({ memoryView: eventView, date: occurredAt.slice(0, 10) });
      }

      if (row.missionCompleted === true || row.mission_completed === true) {
        var completedAt = clean(
          row.missionCompletedAt || row.mission_completed_at || row.completed_at
        );
        var completedYear = passportYearOf(completedAt);
        if (completedYear) {
          var completedRecord = recordFor(completedYear);
          completedRecord.summary.growth += 1;
          completedRecord.events.push({ memoryView: 'growth', date: completedAt.slice(0, 10) });
        }
      }
    });

    Object.keys(records).forEach(function (year) {
      records[year].events.sort(function (a, b) {
        return String(b.date || '').localeCompare(String(a.date || ''));
      });
    });

    return { years: Object.keys(records).sort(), records: records };
  }

  // One read-only Living CV projection; persistence and Core rights remain separate.
  function livingCvSnapshot() {
    var session = window.LittleGWebSession;
    var state =
      session && typeof session.getState === 'function' ? session.getState() : { status: 'guest' };
    var subject = state.status === 'authenticated' ? clean(state.profile && state.profile.id) : '';
    var synchronized =
      window.LittleGProfile &&
      window.LittleGProfile.livingCV &&
      window.LittleGProfile.livingCV.snapshot();
    if (subject && synchronized && synchronized.owner === subject && synchronized.authorized) {
      var cloudItems = synchronized.records
        .filter(function (row) {
          return (
            !row.deleted_at &&
            ['experience', 'skill', 'portfolio', 'achievement'].indexOf(row.record_type) >= 0
          );
        })
        .map(function (row) {
          var date = row.content.date || row.created_at || row.updated_at;
          var stamp = Date.parse(date);
          return {
            id: row.record_id,
            record_type: row.record_type,
            source_id: row.record_id,
            source_type: 'living-cv',
            saved_at: Number.isFinite(stamp) ? new Date(stamp).toISOString() : '',
            year: Number.isFinite(stamp)
              ? String(new Date(stamp).getUTCFullYear())
              : String(new Date().getUTCFullYear()),
            title: row.content.title || row.record_type,
            description: row.content.description || '',
            persistence_status: row.status,
            memoryViews: ['growth'],
            memoryView: 'growth',
            isFriend: false,
            relationship: '',
            mission_completed: false,
          };
        })
        .sort(function (a, b) {
          return b.saved_at.localeCompare(a.saved_at);
        });
      return {
        status: synchronized.hydrated ? 'ready' : 'unavailable',
        records: cloudItems,
        years: Array.from(
          new Set(
            cloudItems.map(function (r) {
              return r.year;
            })
          )
        )
          .sort()
          .reverse(),
      };
    }
    var localProfile =
      window.LittleGProfile && typeof window.LittleGProfile.get === 'function'
        ? window.LittleGProfile.get()
        : null;
    var localId = clean(localProfile && localProfile.profile_id);
    var items = [],
      seen = Object.create(null),
      unavailable = !!subject && cvCloudState !== 'ready';
    if (['booting', 'unknown', 'error'].indexOf(state.status) >= 0)
      return { status: 'unavailable', records: [], years: [] };
    function owns(row) {
      var ownerSubject = clean(row.owner_subject || row.littleg_subject || row.subject);
      var ownerLocal = clean(row.owner_profile_id || row.profile_id);
      if (ownerSubject) return !!subject && ownerSubject === subject;
      if (ownerLocal) return !!localId && ownerLocal === localId && !subject;
      // Anonymous legacy records remain device-local; logging in never claims them.
      return !subject;
    }
    function add(row, source, date) {
      var id = clean(row.record_id || row.result_id || row.card_id || row.id);
      var stamp = Date.parse(date);
      if (
        !id ||
        !Number.isFinite(stamp) ||
        /^(draft|preview|pending|deleted)$/.test(clean(row.status))
      )
        return;
      var key = source + ':' + id;
      if (seen[key]) return;
      seen[key] = true;
      items.push({
        id: key,
        source_id: id,
        source_type: source,
        saved_at: new Date(stamp).toISOString(),
        year: String(new Date(stamp).getUTCFullYear()),
        persistence_status: 'saved',
        title: clean(row.title || row.mission_name || row.activity_name).slice(0, 240),
        description: clean(row.memory_note || row.today_diary || row.description).slice(0, 2000),
        memoryViews: passportMemoryViews(row),
        isFriend: clean(row.type || row.kind) === 'friend',
        relationship: clean(row.relationship),
        mission_completed: row.missionCompleted === true || row.mission_completed === true,
        memoryView: source === 'city-card' ? 'growth' : passportMemoryViews(row)[0] || 'family',
      });
    }
    try {
      var owner = storageOwner();
      if (!owner || typeof owner.listAll !== 'function')
        throw new Error('saved_records_unavailable');
      owner.listAll({ strict: true }).forEach(function (row) {
        if (!row || !owns(row)) return;
        var familyRecord = isFamilyRecord(row);
        if (
          familyRecord &&
          (!ParentGate.isAuthorized('family-sensitive-action') || row.parent_managed !== true)
        )
          return;
        if (!familyRecord && (typeof owner.isCityCard !== 'function' || !owner.isCityCard(row)))
          return;
        add(
          row,
          familyRecord ? 'family-record' : 'city-card',
          clean(row.saved_at || row.confirmed_at)
        );
      });
    } catch (_) {
      unavailable = true;
    }
    try {
      readSocialPlaces(true).forEach(function (row) {
        if (
          row.visibility !== 'family_private' ||
          !row.privacyConfirmed ||
          !row.parentConfirmed ||
          row.approvalStatus !== 'approved'
        )
          return;
        var cloudOwned = !!subject && cvCloudSubject === subject && !!row.cloudEntityId;
        if (!cloudOwned && (!owns(row) || !ParentGate.isAuthorized('family-sensitive-action')))
          return;
        add(row, 'family-memory', clean(row.createdAt || row.created_at));
      });
    } catch (_) {
      unavailable = true;
    }
    items.sort(function (a, b) {
      return b.saved_at.localeCompare(a.saved_at);
    });
    return {
      status: unavailable ? 'unavailable' : 'ready',
      records: items,
      years: Array.from(
        new Set(
          items.map(function (r) {
            return r.year;
          })
        )
      )
        .sort()
        .reverse(),
    };
  }

  var SocialGraph = Object.freeze({
    storageMode: 'cloud_authoritative_local_cache',
    storageOwner: 'workers/gcity-core-api/src/routes/family-memory.js',
    cacheOwner: 'assets/littleg-family-data.js',
    cloudContract: 'customer_memory_v1',
    key: SOCIAL_KEY,
    maxRows: SOCIAL_MAX_ROWS,
    locationTypes: SOCIAL_LOCATION_TYPES,
    memoryViews: SOCIAL_MEMORY_VIEWS,
    listPlaces: readSocialPlaces,
    getPlace: socialGetPlace,
    upsertPlace: socialUpsertPlace,
    removePlace: socialRemovePlace,
    stationIndexReady: function () {
      return loadStationIndex().then(function (index) {
        return Object.keys(index).length > 0;
      });
    },
    ready: hydrateSocialGraphFromCloud,
    syncNow: function () {
      return hydrateSocialGraphFromCloud(true);
    },
    replacePlaces: function (rows) {
      return writeSocialPlaces(Array.isArray(rows) ? rows : [], 'replace');
    },
    clearPlaces: function () {
      return writeSocialPlaces([], 'clear');
    },
    findByLandmarkId: socialFindByLandmarkId,
    shareSummary: socialShareSummary,
    markMissionComplete: socialMarkMissionComplete,
    passportSnapshot: socialPassportSnapshot,
  });

  function normalizeRecord(type, payload) {
    var safeType = ALLOWED_RECORD_TYPES.indexOf(type) !== -1 ? type : 'mission';
    var source = payload && typeof payload === 'object' ? payload : {};
    var id = clean(source.result_id || source.record_id || source.card_id || source.id);
    if (!id)
      id =
        'family-' +
        safeType +
        '-' +
        Date.now().toString(36) +
        '-' +
        Math.random().toString(36).slice(2, 8);
    var routeReadiness =
      safeType === 'notice' ? normalizeRouteReadiness(source.route_readiness, id) : null;
    return Object.assign({}, source, {
      schema_version:
        safeType === 'notice' ? 'littleg_family_notice_v1' : 'littleg_family_mission_v1',
      result_id: id,
      record_id: id,
      card_id: id,
      record_type: safeType,
      visibility: 'private',
      record_visibility: 'private',
      parent_managed: true,
      parent_gate_required: true,
      precise_location_saved: false,
      public_publication: false,
      formal_submission_status: 'not_submitted',
      backend_submission_status: clean(source.backend_submission_status) || 'not_submitted',
      next_destination: 'cards',
      lead_type: 'family_' + safeType,
      lead_category: safeType === 'notice' ? 'family_notice' : 'family_city_exploration',
      source: clean(source.source) || 'littleg-family',
      route_readiness: routeReadiness,
      saved_at: clean(source.saved_at) || new Date().toISOString(),
    });
  }

  function isFamilyRecord(entry) {
    if (!entry || typeof entry !== 'object') return false;
    var schema = clean(entry.schema_version).toLowerCase();
    var path = clean(entry.storage_path).toLowerCase();
    var source = clean(entry.source).toLowerCase();
    return (
      schema.indexOf('littleg_family_') === 0 ||
      path.indexOf('family-') === 0 ||
      source.indexOf('littleg-family') === 0
    );
  }

  function storageOwner() {
    var owner = window.LittleGResultStorage;
    return owner && typeof owner.saveResult === 'function' ? owner : null;
  }

  var ParentGate = Object.freeze({
    VERSION: VERSION,
    ttlMs: GATE_TTL_MS,
    isAuthorized: function (scope) {
      var state = readGate();
      if (!state) return false;
      var requested = clean(scope || 'family-sensitive-action');
      return state.scope === '*' || state.scope === requested;
    },
    authorize: function (options) {
      var settings = options && typeof options === 'object' ? options : {};
      if (settings.confirmed !== true) return false;
      var state = {
        scope: clean(settings.scope || 'family-sensitive-action'),
        source: clean(settings.source || 'family-ui'),
        authorized_at: Date.now(),
        expires_at: Date.now() + GATE_TTL_MS,
      };
      var saved = writeGate(state);
      if (saved) {
        emit('littleg:parent-gate-change', {
          authorized: true,
          scope: state.scope,
          expires_at: state.expires_at,
        });
      }
      return saved;
    },
    clear: function () {
      var store = safeSession();
      try {
        if (store) store.removeItem(GATE_KEY);
      } catch (_) {}
      emit('littleg:parent-gate-change', { authorized: false });
      return true;
    },
    request: function (scope, detail) {
      var source = detail && typeof detail === 'object' ? detail : {};
      var payload = {
        scope: clean(scope || 'family-sensitive-action'),
        authorized: false,
        source: clean(source.source || 'family-policy'),
        reason: clean(source.reason).slice(0, 120) || null,
        action: clean(source.action).slice(0, 120) || null,
      };
      emit('littleg:parent-gate-required', payload);
      return payload;
    },
  });

  var FamilyData = Object.freeze({
    VERSION: VERSION,
    owner: 'assets/littleg-family-data.js',
    persistenceOwner: 'assets/littleg-city-result-storage.js',
    draftPersistenceOwner: 'assets/littleg-family-data.js',
    socialGraphPersistenceOwner: 'workers/gcity-core-api/src/routes/family-memory.js',
    socialGraphCacheOwner: 'assets/littleg-family-data.js',
    draftTtlMs: DRAFT_TTL_MS,
    privacyDefaults: Object.freeze({
      visibility: 'private',
      preciseLocationSaved: false,
      publicPublication: false,
      parentManaged: true,
    }),
    livingCvSnapshot: livingCvSnapshot,
    socialGraph: SocialGraph,
    normalizeRecord: normalizeRecord,
    normalizeRouteReadiness: normalizeRouteReadiness,
    isFamilyRecord: isFamilyRecord,
    saveNoticeDraft: writeDraft,
    getNoticeDraft: readDraft,
    clearNoticeDraft: clearDraft,
    saveRecord: function (type, payload) {
      var owner = storageOwner();
      var record = normalizeRecord(type, payload);
      if (!owner) {
        emit('littleg:family-record-save-failed', {
          reason: 'canonical_storage_owner_unavailable',
          record_type: record.record_type,
          record_id: clean(record.record_id).slice(0, 120),
          visibility: 'private',
        });
        return null;
      }
      var saved = owner.saveResult(record, 'family-' + record.record_type);
      emit('littleg:family-record-saved', {
        saved: !!saved,
        record_type: record.record_type,
        record_id: clean((saved || record).record_id).slice(0, 120),
        visibility: 'private',
      });
      return saved;
    },
    listRecords: function () {
      var owner = storageOwner();
      if (!owner || typeof owner.listAll !== 'function') return [];
      return owner.listAll().filter(isFamilyRecord);
    },
  });

  window.LittleGParentGate = ParentGate;
  window.LittleGFamilyData = FamilyData;

  document.addEventListener('littleg:web-session-change', function () {
    cvCloudSubject = '';
    cvCloudState = 'loading';
    var liveSession = window.LittleGWebSession;
    var status = clean(liveSession && liveSession.getState && liveSession.getState().status);
    if (status === 'authenticated') {
      cloudSessionGeneration += 1;
      cloudHydratePromise = null;
      hydrateSocialGraphFromCloud(true);
      return;
    }
    if (status === 'guest') {
      transitionSocialGraphToGuest();
    }
  });

  document.addEventListener('littleg:map-passport-request', function (event) {
    var target = event && event.target;
    if (!target || !target.matches || !target.matches('[data-map-passport]')) return;
    var requestGeneration = cloudSessionGeneration;
    var cv =
      window.LittleGProfile &&
      window.LittleGProfile.livingCV &&
      window.LittleGProfile.livingCV.snapshot();
    Promise.resolve(cv && cv.owner && cv.authorized ? undefined : SocialGraph.ready())
      .then(function () {
        if (requestGeneration !== cloudSessionGeneration || target.isConnected === false) return;
        var snapshot = livingCvSnapshot(),
          records = Object.create(null);
        snapshot.records.forEach(function (item) {
          if (!records[item.year]) records[item.year] = emptyPassportYearRecord();
          var record = records[item.year];
          record.events.push(Object.assign({}, item, { date: item.saved_at.slice(0, 10) }));
          if (item.source_type === 'family-memory') {
            if (item.isFriend) record.summary.friends += 1;
            if (item.memoryViews.indexOf('alumni') >= 0 || item.relationship === 'alumni')
              record.summary.alumni += 1;
            if (item.memoryViews.indexOf('class') >= 0 || item.relationship === 'classmate')
              record.summary.classes += 1;
            if (item.memoryViews.indexOf('travel') >= 0) record.summary.travel += 1;
            if (item.memoryViews.indexOf('family') >= 0 || item.relationship === 'family')
              record.summary.family += 1;
            if (item.mission_completed) record.summary.growth += 1;
          } else record.summary.growth += 1;
        });
        target.dispatchEvent(
          new CustomEvent('littleg:map-passport-data', {
            bubbles: false,
            detail: { status: snapshot.status, years: snapshot.years, records: records },
          })
        );
      })
      .catch(function () {
        target.dispatchEvent(
          new CustomEvent('littleg:map-passport-data', {
            bubbles: false,
            detail: { status: 'unavailable', years: [], records: {} },
          })
        );
      });
  });

  hydrateSocialGraphFromCloud(false);

  window.addEventListener('storage', function (event) {
    if (!event || event.key !== SOCIAL_KEY) return;
    emit('littleg:family-social-graph-change', {
      reason: 'external-tab',
      count: readSocialPlaces().length,
      changed_id: null,
    });
  });

  emit('littleg:family-data-ready', {
    version: VERSION,
    persistence_owner: FamilyData.persistenceOwner,
    draft_persistence_owner: FamilyData.draftPersistenceOwner,
    social_graph_persistence_owner: FamilyData.socialGraphPersistenceOwner,
    social_graph_cache_owner: FamilyData.socialGraphCacheOwner,
  });
})(window, document);
